import { create } from 'zustand';
import {
  onAuthStateChanged,
  signOut as fbSignOut,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '@/services/firebase';
import {
  ensureDefaultUserDoc,
  type UserDoc,
} from '@/services/firestore/users';
import { clearRememberedCreds } from '@/lib/remember';
import { useFavoritesStore } from '@/stores/favorites';

export type UserProfile = UserDoc & { uid: string };

type AuthState = {
  isInitializing: boolean;
  isLoggedIn: boolean;
  profile: UserProfile | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendReset: (email: string) => Promise<void>;
};

export const useAuthStore = create<AuthState>(() => ({
  isInitializing: true,
  isLoggedIn: false,
  profile: null,
  signIn: async (email, password) => {
    await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
  },
  signOut: async () => {
    clearRememberedCreds();
    useFavoritesStore.getState().clearForLogout();
    await fbSignOut(auth);
  },
  sendReset: async (email) => {
    await sendPasswordResetEmail(auth, email.trim().toLowerCase());
  },
}));

onAuthStateChanged(auth, async (user) => {
  if (!user) {
    useAuthStore.setState({
      isInitializing: false,
      isLoggedIn: false,
      profile: null,
    });
    return;
  }
  try {
    const doc = await ensureDefaultUserDoc(user.uid, user.email ?? '');
    useAuthStore.setState({
      isInitializing: false,
      isLoggedIn: true,
      profile: { ...doc, uid: user.uid },
    });
    // Hydrate Firestore-backed prefs in the background — store has the local
    // cache loaded already so the UI doesn't wait on the round-trip.
    void useFavoritesStore.getState().load(user.uid);
  } catch (err) {
    console.error('failed to load user profile', err);
    useAuthStore.setState({
      isInitializing: false,
      isLoggedIn: true,
      profile: null,
    });
  }
});
